import { env } from '@/env';
import { isNative, showPushToast } from '@/shared/bridge';
import type { Navigator } from '@/shared/navigation';
import { ROUTES, resolvePushRoute } from '@/shared/routing';

/**
 * 모바일 브라우저(= WebView 아닌 순수 모바일 브라우저) 판정.
 * UA-CH `userAgentData.mobile` 우선, 미지원 브라우저(Safari/Firefox)는 UA 문자열 폴백.
 * iPadOS 13+ 는 데스크톱 Safari 로 위장하므로 터치 포인트로 별도 식별.
 */
function isMobileBrowser(): boolean {
  const nav = window.navigator as unknown as {
    userAgent: string;
    maxTouchPoints: number;
    userAgentData?: { mobile?: boolean };
  };
  if (typeof nav.userAgentData?.mobile === 'boolean') return nav.userAgentData.mobile;
  const ua = nav.userAgent;
  if (/Android|iPhone|iPod|iPad|Windows Phone|BlackBerry|Opera Mini|IEMobile|Mobile/i.test(ua)) {
    return true;
  }
  return /Macintosh/.test(ua) && nav.maxTouchPoints > 1;
}

/**
 * 이미 등록돼 있던 FCM service worker 를 해제 — push subscription 도 함께 폐기된다.
 * 정책 변경(모바일 브라우저 push 중단) 이전에 권한을 허용했던 사용자의 잔존 구독 정리용.
 */
function unregisterWebPushSw(): void {
  void window.navigator.serviceWorker
    .getRegistrations()
    .then((registrations) => {
      for (const registration of registrations) {
        if (registration.active?.scriptURL.includes('firebase-messaging-sw.js')) {
          void registration.unregister();
        }
      }
    })
    .catch(() => {}); // fail-open — 정리 실패해도 앱 동작에 영향 없음.
}

export interface WebPushDeps {
  navigator: Navigator;
  /** 발급된 web FCM 토큰. 서버 등록은 호출자(fcm-sync)가 native 와 동일 경로로 처리. */
  onFcmToken: (fcmToken: string) => void;
}

/**
 * 브라우저 web push 초기화 (FCM JS SDK). native 와 대칭 — native 는 bridge 로, web 은 본 모듈로
 * 토큰을 `fcm-sync` 에 공급하고 `POST /api/v3/auth/fcm` 은 코디네이터가 통합 처리.
 *
 *   - WebView(native)에선 skip — native FCM 이 푸시 소유(이중 등록 방지).
 *   - **모바일 브라우저에서도 skip** — 모바일 표면의 푸시는 앱(native)이 담당. 브라우저 web push 는
 *     데스크톱 전용(iOS Safari 는 홈화면 설치 PWA 만 지원해 사실상 불가하기도 함). 과거 허용
 *     사용자의 잔존 구독은 SW unregister 로 정리.
 *   - firebase/vapidKey 미설정 시 no-op (native.* 빌드 · prod 미등록 등).
 *   - 권한 요청 = **토큰 생성 시점**(본 init, 앱과 동일). Safari 는 user gesture 필요할 수 있어
 *     거부/실패 시 fail-open(토큰 없음 → 등록 안 함). firebase SDK 는 dynamic import 로 code-split.
 *   - 알림 탭 → SW 가 클릭 이벤트를 postMessage, 여기서 `resolvePushRoute` 로 navigate (라우팅 SSOT=web).
 */
export function initWebPush({ navigator, onFcmToken }: WebPushDeps): void {
  if (isNative()) return; // WebView 는 native FCM 소유.
  const cfg = env.firebase;
  if (!cfg?.vapidKey) return; // VAPID 미설정 → web push 비활성.
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in window.navigator) || !('Notification' in window)) return;
  if (isMobileBrowser()) {
    unregisterWebPushSw(); // 모바일 표면 푸시 = 앱(native) 담당. 과거 구독만 정리하고 종료.
    return;
  }

  void (async () => {
    try {
      // ⚠️ `getToken` 은 @firebase/messaging 0.13 부터 deprecated — 대체재는 FID(Firebase
      // Installation ID) 기반 `register()` + `onRegistered()` 콜백. 하지만 FID 는 **BE 가
      // FID 타겟 전송을 지원해야** 도달하고(FCM registration token 과 다른 식별자),
      // `POST /api/v3/auth/fcm` 이 이를 수용·전송하는지 미확인이라 전환 보류.
      // 지금은 동작이 검증된 registration token 을 유지한다(IDE 취소선은 의도된 잔존).
      // BE 확인되면 register/onRegistered + unregister/onUnregistered 로 일괄 전환. (ADR §미해결)
      const [{ initializeApp }, { getMessaging, getToken, onMessage, isSupported }] =
        await Promise.all([import('firebase/app'), import('firebase/messaging')]);
      if (!(await isSupported())) return;

      // SW 등록 — config 를 쿼리로 주입(SW 가 env 무관하게 읽음).
      const swQuery = new URLSearchParams({
        apiKey: cfg.apiKey,
        authDomain: cfg.authDomain,
        projectId: cfg.projectId,
        storageBucket: cfg.storageBucket,
        messagingSenderId: cfg.messagingSenderId,
        appId: cfg.appId,
      }).toString();
      const registration = await window.navigator.serviceWorker.register(
        `/firebase-messaging-sw.js?${swQuery}`,
      );

      const app = initializeApp({
        apiKey: cfg.apiKey,
        authDomain: cfg.authDomain,
        projectId: cfg.projectId,
        storageBucket: cfg.storageBucket,
        messagingSenderId: cfg.messagingSenderId,
        appId: cfg.appId,
        measurementId: cfg.measurementId,
      });
      const messaging = getMessaging(app);

      // 권한 요청 == 토큰 생성 시점(앱과 동일). 거부 시 fail-open.
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;

      const fcmToken = await getToken(messaging, {
        vapidKey: cfg.vapidKey,
        serviceWorkerRegistration: registration,
      });
      if (fcmToken) onFcmToken(fcmToken);

      // 포그라운드 수신(탭 활성) → 인앱 토스트(web·native 공용 정책). 백그라운드는 SW 가 브라우저 자동표시에 위임.
      onMessage(messaging, (payload) => {
        const data = payload.data ?? {};
        showPushToast(navigator, {
          title: payload.notification?.title ?? data.title,
          body: payload.notification?.body ?? data.body,
          data,
        });
      });

      // SW 알림 클릭 → 라우팅 (native push.notificationOpened 와 대칭).
      window.navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
        if (event.data?.type === 'push-notification-click') {
          const route = resolvePushRoute(event.data.data ?? {});
          navigator.push(route ?? ROUTES.DASHBOARD);
        }
      });
    } catch (e) {
      console.warn('[web-push] init failed', e);
    }
  })();
}
